import {
  f7,
  Icon,
  Link,
  List,
  ListItem,
  Preloader,
  SwipeoutActions,
  SwipeoutButton,
} from "framework7-react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { requests } from "../../constants/requests";
import { useIsPending } from "../../hooks/use-is-pending";
import { MenuItem, MenuList, MenuSection } from "../../components/menu";
import { useToast } from "../../hooks/use-toast";
import { getGroupStatus, removeGroup } from "../../redux/reducer/app-slice";
import { confirmDialog } from "../../utils/confirm-dialog";

const noop = () => {};

const SKELETON_ROWS = ["skeleton-1", "skeleton-2"];

const CREATE_ICON = { ios: "f7:plus", md: "material:add" };
const JOIN_ICON = {
  ios: "f7:person_badge_plus_fill",
  md: "material:group_add",
};

const GroupList = ({
  groups = [],
  closeParentPanel = noop,
  onCreate = noop,
  onJoin = noop,
}) => {
  const dispatch = useDispatch();
  const { showError } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const editing = isEditing && groups.length > 0;

  const isLoadingGroups = useIsPending(requests.loadGroups);
  const isRemoving = useIsPending(requests.removeGroup);

  useEffect(() => {
    if (!isRemoving && !isLoadingGroups) setRemovingId(null);
  }, [isRemoving, isLoadingGroups]);
  const removeLocked = isRemoving || removingId !== null;

  useEffect(() => {
    const endEditing = () => setIsEditing(false);
    f7.on("panelClosed", endEditing);
    return () => f7.off("panelClosed", endEditing);
  }, []);

  const handleGroupSwitch = (groupId) => {
    dispatch(getGroupStatus(groupId, false, noop, showError));
    closeParentPanel();
  };

  const leaveGroup = (groupId) => {
    setRemovingId(groupId);
    dispatch(
      removeGroup(groupId, noop, (errorMessage) => {
        setRemovingId(null);
        showError(errorMessage);
      }),
    );
  };

  const confirmLeaveGroup = (group) => {
    if (removeLocked) return;
    confirmDialog({
      title: group.name,
      text: "Möchtest du die Gruppe wirklich entfernen?",
      confirmText: "Entfernen",
      destructive: true,
      cssClass: "leave-group-dialog",
      onConfirm: () => leaveGroup(group.id),
      onClosed: () => f7.swipeout.close(".group-list .swipeout-opened"),
    });
  };

  const showList = groups.length > 0 || isLoadingGroups;

  return (
    <MenuSection
      title="Gruppen"
      className="groups-menu"
      after={
        <>
          {isLoadingGroups && (
            <Preloader
              className="group-list-preloader"
              aria-label="Gruppen werden geladen"
            />
          )}
          {groups.length > 0 && (
            <Link
              className="group-list-edit-toggle"
              onClick={() => setIsEditing(!editing)}
            >
              {editing ? "Fertig" : "Bearbeiten"}
            </Link>
          )}
        </>
      }
    >
      {showList && (
        <List
          inset
          strong
          dividersIos
          className={`side-menu-list group-list no-swipe-panel${editing ? " group-list-editing" : ""}`}
        >
          {groups.length === 0 &&
            isLoadingGroups &&
            SKELETON_ROWS.map((key) => (
              <ListItem
                key={key}
                className="group-list-skeleton skeleton-text skeleton-effect-wave"
                title="Gruppenname"
              />
            ))}
          {groups.map((group) => {
            const isRemovingThis = removingId === group.id;
            return (
              <ListItem
                key={group.id}
                link={!editing}
                swipeout
                className={isRemovingThis ? "group-removing" : undefined}
                onClick={
                  editing ? undefined : () => handleGroupSwitch(group.id)
                }
                title={group.name}
              >
                {editing && (
                  <Link
                    slot="after"
                    className={`group-remove-button${removeLocked ? " disabled" : ""}`}
                    color="red"
                    iconOnly
                    aria-label={`${group.name} entfernen`}
                    onClick={() => confirmLeaveGroup(group)}
                  >
                    {isRemovingThis ? (
                      <Preloader size={20} color="red" />
                    ) : (
                      <Icon ios="f7:trash" md="material:delete" />
                    )}
                  </Link>
                )}
                <SwipeoutActions right>
                  <SwipeoutButton
                    color="red"
                    className={removeLocked ? "disabled" : undefined}
                    onClick={() => confirmLeaveGroup(group)}
                  >
                    Entfernen
                  </SwipeoutButton>
                </SwipeoutActions>
              </ListItem>
            );
          })}
        </List>
      )}
      <MenuList className="group-actions-list">
        <MenuItem
          className="group-create-entry"
          title="Neue Gruppe erstellen"
          icon={CREATE_ICON}
          tone="theme"
          onClick={onCreate}
        />
        <MenuItem
          className="group-join-entry"
          title="Gruppe beitreten"
          icon={JOIN_ICON}
          tone="green"
          onClick={onJoin}
        />
      </MenuList>
    </MenuSection>
  );
};

export default GroupList;
